import flatten from "../../helpers/flatten";
import partition from "../../../../common/helpers/partition";
import Transcript from "./models/Transcript";
import packLargeRegions from "../../helpers/packRegions/packLargeRegions";
import getFilters from "./getFilters";
import bisectRegion from "../../helpers/binarySearch/bisectRegion";
import createOverlapChunks from "../../helpers/binarySearch/createOverlapChunks";
import { TRANSCRIPT_CL } from "../../constants/options";
import {
  getSequenceMesh,
  drawSequence,
  updateSeqVis,
} from "./sequenceBlueprint";

import {
  getCodonMesh,
  getCodonTextMesh,
  drawTranscript,
  updateTranscriptsVis,
  getTranscriptsMesh,
  drawCodon,
  drawCodonText,
  updateMeshUvMap,
} from "./transcriptsBlueprint";
import ColorScale from "../../models/ColorScale";
import {
  sequenceRgbIndex,
  TRIPLO_HAPLO,
  TRIPLO,
  HAPLO,
  NORMAL,
  sequenceTypeIndex,
} from "../../constants/sequences";

const floorHeight = 55;

function getDefaultState() {
  return {
    geneDict: new Map(),
    trChunks: [],
    trSortedStart: [],
    levels: [],
    transcriptsAll: [],
    transcriptsRight: [],
    transcriptsLeft: [],
    sequencesLeft: [],
    sequencesRight: [],
    exonsRight: [],
    exonsLeft: [],
    codonsLeft: [],
    codonsRight: [],
    allCodons: [],
    //
    transcriptDict: new Map(),
    visRange: [0, Infinity],
    filters: getFilters(),
    selectedTranscript: null,
  };
}

function transcriptTrackController(track) {
  let state = getDefaultState();
  const transcriptsRightMesh = getTranscriptsMesh(false, floorHeight);
  const transcriptsLeftMesh = getTranscriptsMesh(true, floorHeight);
  const exonsRightMesh = getSequenceMesh(false, floorHeight, true);
  const exonsLeftMesh = getSequenceMesh(true, floorHeight, true);
  const sequencesRightMesh = getSequenceMesh(false, floorHeight);
  const sequencesLeftMesh = getSequenceMesh(true, floorHeight);
  const codonsLeftMesh = getCodonMesh(true, floorHeight);
  const codonsRightMesh = getCodonMesh(false, floorHeight);
  const codonTextsMesh = getCodonTextMesh(floorHeight);

  function onReset() {
    state = getDefaultState();
  }

  function onInit() {
    track.setFilters([...state.filters]);
  }

  function onRangeChange(props) {
    const { domainDist, rangeDist, chrSegments, start, end } = props;
    const baseWidth = rangeDist / domainDist;
    const isZoomedInCodons = baseWidth > 1;
    const isZoomedInText = baseWidth > 12;
    const isZoomedInExons = domainDist < 5000000;
    const seqs = chrSegments.map((x) => ({
      ...x,
      x0: x.chr.region.x0,
      x1: x.chr.region.x1,
      g0: x.chr.region.g0,
      g1: x.chr.region.g1,
    }));

    const switchedCodons = codonsLeftMesh.isVisible !== isZoomedInCodons;
    const switchedExons = exonsLeftMesh.isVisible !== isZoomedInExons;

    codonsLeftMesh.setIsVisible(isZoomedInCodons);
    codonsRightMesh.setIsVisible(isZoomedInCodons);
    codonTextsMesh.setIsVisible(isZoomedInText);
    exonsLeftMesh.setIsVisible(isZoomedInExons);
    exonsRightMesh.setIsVisible(isZoomedInExons);
    sequencesLeftMesh.setIsVisible(isZoomedInExons);
    sequencesRightMesh.setIsVisible(isZoomedInExons);

    track.loadPosition(seqs);
    state.visRange = [start, end];

    if (switchedCodons) updateVisibleCodons();
    if (switchedExons) onPreferencesChanged();
    if (switchedExons) {
      updateMeshUvMap(transcriptsLeftMesh, isZoomedInExons);
      updateMeshUvMap(transcriptsRightMesh, isZoomedInExons);
    }
  }

  function partitionTranscripts(arr = []) {
    const isLeft = (x) => x.strand === -1;
    const [rightTr, leftTr] = partition(arr, isLeft);
    return { rightTr, leftTr };
  }

  function process(transcripts, chr) {
    return transcripts
      .filter(Boolean)
      .map((x) => new Transcript(x, chr))
      .map((x) => {
        if (state.transcriptDict.get(x.id)) return null;
        state.transcriptDict.set(x.id, x);
        return x;
      })
      .filter(Boolean);
  }

  function onDataReceived({ res, chr }) {
    if (!res || (res && !(res.length > 0))) return;
    const newTranscripts = process(res, chr);
    const { rightTr, leftTr } = partitionTranscripts(newTranscripts);
    const sequencesLeft = flatten(leftTr.map((x) => x.sequences));
    const sequencesRight = flatten(rightTr.map((x) => x.sequences));

    const exonsLeft = flatten(leftTr.map((x) => x.exons));
    const exonsRight = flatten(rightTr.map((x) => x.exons));

    const codonsLeft = flatten(exonsLeft.map((x) => x.codons));
    const codonsRight = flatten(exonsRight.map((x) => x.codons));

    state.transcriptsAll = state.transcriptsAll.concat(newTranscripts);

    newTranscripts.reduce((a, i) => {
      const existing = (a.get(i.geneSymbol) || []).concat([i]);
      return a.set(i.geneSymbol, existing);
    }, state.geneDict);

    //
    state.trSortedStart = state.trSortedStart.concat(newTranscripts);
    state.trSortedStart.sort((a, b) => a.region.g0 - b.region.g0);
    state.trChunks = createOverlapChunks(state.trSortedStart);

    //
    state.transcriptsLeft = state.transcriptsLeft.concat(leftTr);
    state.transcriptsRight = state.transcriptsRight.concat(rightTr);
    state.sequencesLeft = state.sequencesLeft.concat(sequencesLeft);
    state.sequencesRight = state.sequencesRight.concat(sequencesRight);
    state.exonsLeft = state.exonsLeft.concat(exonsLeft);
    state.exonsRight = state.exonsRight.concat(exonsRight);
    state.codonsLeft = state.codonsLeft.concat(codonsLeft);
    state.codonsRight = state.codonsRight.concat(codonsRight);

    transcriptsLeftMesh.setItems(state.transcriptsLeft);
    transcriptsRightMesh.setItems(state.transcriptsRight);
    sequencesRightMesh.setItems(state.sequencesRight);
    sequencesLeftMesh.setItems(state.sequencesLeft);
    exonsLeftMesh.setItems(state.exonsLeft);
    exonsRightMesh.setItems(state.exonsRight);

    resort();
    applyFiltersToTranscripts(newTranscripts);

    drawTranscript(leftTr, transcriptsLeftMesh);
    drawTranscript(rightTr, transcriptsRightMesh);
    drawSequence(sequencesLeft, sequencesLeftMesh);
    drawSequence(sequencesRight, sequencesRightMesh);
    drawSequence(exonsLeft, exonsLeftMesh);
    drawSequence(exonsRight, exonsRightMesh);

    calcFilterCounts(newTranscripts);
    track.setFilters([...state.filters]);
    updateVisibleCodons();
    update();
  }

  function getFilterDict() {
    return state.filters.reduce((a, i) => ({ ...a, [i.key]: i }), {});
  }

  function applyFiltersToTranscripts(trans) {
    const filters = getFilterDict();

    const { canonical, maneSelect, haplo, triplo, haploTriplo, normal } =
      filters;
    const { ensembl, refSeq } = filters;

    // main filters
    const hasCan = canonical.value;
    const hasMane = maneSelect.value;
    const hasRefSeq = refSeq.value;
    const hasEnsembl = ensembl.value;
    const hasType = Number(hasCan) + Number(hasMane);
    const hasDb = Number(hasRefSeq) + Number(hasEnsembl);

    const hasHaplo = haplo.value;
    const hasTriplo = triplo.value;
    const hasHaploTriplo = haploTriplo.value;
    const hasNormal = normal.value;
    const hasSensitivity = hasHaplo + hasTriplo + hasHaploTriplo + hasNormal;

    for (let i = 0; i < trans.length; i++) {
      const x = trans[i];

      if (x.isExcluded) continue;

      const isBoth = x.triploHaploIndex === 6;
      const isHaplo = x.triploHaploIndex === 4 || isBoth;
      const isTriplo = x.triploHaploIndex === 5 || isBoth;

      const isACMG = x.transcriptName === state.selectedTranscript;
      if (isACMG) {
        x.hidden = false;
      } else {
        const isTypeIn =
          !hasType || (hasCan && x.isCanonical) || (hasMane && x.isMane);
        const isDbIn =
          !hasDb || (hasRefSeq && x.isRefSeq) || (hasEnsembl && x.isEnsembl);

        const isSensitiveIn =
          !hasSensitivity ||
          (hasHaplo && isHaplo) ||
          (hasTriplo && isTriplo) ||
          (hasHaploTriplo && isBoth) ||
          (hasNormal && !isHaplo && !isTriplo && !isBoth);

        x.hidden = !isTypeIn || !isDbIn || !isSensitiveIn;
      }
    }
    updatePositions();
  }

  function updatePositions() {
    const { transcriptsAll: all } = state;
    state.levels = packLargeRegions(all, [], 0, true, Infinity, -50000, 1000);
    updateTranscriptsVis(state.transcriptsLeft, transcriptsLeftMesh);
    updateTranscriptsVis(state.transcriptsRight, transcriptsRightMesh);
    updateSeqVis(state.sequencesLeft, sequencesLeftMesh);
    updateSeqVis(state.sequencesRight, sequencesRightMesh);
    updateSeqVis(state.exonsLeft, exonsLeftMesh);
    updateSeqVis(state.exonsRight, exonsRightMesh);
  }

  function calcFilterCounts(transcripts) {
    const filters = getFilterDict();
    transcripts.forEach((x) => {
      const hasBoth = x.triploHaploIndex === 6;
      const hasHaplo = x.triploHaploIndex === 4;
      const hasTriplo = x.triploHaploIndex === 5;
      const hasNone = !hasHaplo && !hasTriplo && !hasBoth;

      if (x.isEnsembl) filters.ensembl.totalCount += 1;
      if (hasBoth) filters.haploTriplo.totalCount += 1;
      if (hasNone) filters.normal.totalCount += 1;
      if (hasHaplo) filters.haplo.totalCount += 1;
      if (hasTriplo) filters.triplo.totalCount += 1;
      if (x.isRefSeq) filters.refSeq.totalCount += 1;
      if (x.isCanonical) filters.canonical.totalCount += 1;
      if (x.isMane) filters.maneSelect.totalCount += 1;
    });
  }

  function setRowLimit(limit) {
    const filterDict = getFilterDict();
    filterDict.maxrows.value = Number(limit);
    onFiltersChange([...state.filters]);
  }

  function getVisibleCodons(transcripts) {
    const transcriptCodons = transcripts
      .map((x) => x.calculateCodons())
      .map((x) => x.codons);
    return [].concat(...transcriptCodons);
  }

  function drawCodonsForVisRange(transcripts, visRange) {
    if (codonsLeftMesh.isVisible) {
      const { leftTr, rightTr } = partitionTranscripts(transcripts);
      const leftCodons = getVisibleCodons(leftTr, visRange);
      const rightCodons = getVisibleCodons(rightTr, visRange);
      const allCodons = leftCodons.concat(rightCodons);

      codonsLeftMesh.setItems(leftCodons);
      codonsRightMesh.setItems(rightCodons);
      codonTextsMesh.setItems(allCodons);

      drawCodon(leftCodons, codonsLeftMesh);
      drawCodon(rightCodons, codonsRightMesh);
      drawCodonText(allCodons, codonTextsMesh);
    }
  }

  function findVisibleTranscripts(visRange) {
    const { trSortedStart: sortedTrs, trChunks } = state;
    const s1 = bisectRegion(visRange[0], trChunks, (x) => x.region[1], true);
    const s2 = bisectRegion(visRange[1], trChunks, (x) => x.region[0], false);
    const overlapCandidates = trChunks.slice(s1, s2);

    const firstInd = 0;
    const lastInd = overlapCandidates.length - 1;
    const first = overlapCandidates[firstInd]?.indices || [];
    const last = overlapCandidates[lastInd]?.indices || [];
    const allInd = first.concat(last);
    const ind1 = Math.min(...allInd) || 0;
    const ind2 = Math.max(...allInd) || 0;
    return sortedTrs.slice(ind1, ind2 + 1);
  }

  function updateVisibleCodons() {
    const { visRange } = state;
    if (visRange && exonsLeftMesh.isVisible) {
      const visibleTranscrips = findVisibleTranscripts(visRange);
      drawCodonsForVisRange(visibleTranscrips, visRange);
    }
  }

  function findRegionMaxLevel() {
    const { levels, visRange } = state;
    for (let i = levels.length - 1; i >= 0; i--) {
      const level = levels[i];
      const doesNotFit = !level.check(visRange[0], visRange[1]);
      if (doesNotFit) return i + 1;
    }
    return 1;
  }

  function update() {
    const currentMaxLevel = findRegionMaxLevel();
    const filterDict = getFilterDict();
    const maxRows = Number(filterDict.maxrows.value);
    const maxLevel = Math.min(maxRows, state.levels.length);
    const height = maxLevel * floorHeight;
    const maxY = maxLevel;
    const { selectedTranscript } = state;

    transcriptsLeftMesh.uniforms.setMaxY(maxY);
    transcriptsRightMesh.uniforms.setMaxY(maxY);
    sequencesLeftMesh.uniforms.setMaxY(maxY);
    sequencesRightMesh.uniforms.setMaxY(maxY);
    exonsLeftMesh.uniforms.setMaxY(maxY);
    exonsRightMesh.uniforms.setMaxY(maxY);
    codonsLeftMesh.uniforms.setMaxY(maxY);
    codonsRightMesh.uniforms.setMaxY(maxY);
    codonTextsMesh.uniforms.setMaxY(maxY);
    updateVisibleCodons();

    track.setHeight(height);
    track.requestRender();

    track.setOverlayProps({
      height,
      floorHeight,
      selectedTranscript,
      setRowLimit,
      currentMaxLevel,
      maxLevel,
    });
  }

  function onFiltersChange(filters) {
    state.filters = filters;
    track.setFilters([...state.filters]);
    applyFiltersToTranscripts(state.transcriptsAll);
    update();
  }

  function resort() {
    const { transcriptsAll, selectedTranscript } = state;
    const sel = selectedTranscript || "";
    const mainTr = transcriptsAll.find(
      (x) => !x.isExcluded && x.transcriptName === sel
    );
    const rest = transcriptsAll
      .filter((x) => x !== mainTr)
      .sort((a, b) => a.compareTo(b));

    state.transcriptsAll = [mainTr].concat(rest).filter(Boolean);
  }

  function onSharedStateChange() {
    const { selectedTranscript } = track.getSharedState();
    state.selectedTranscript = selectedTranscript;
    applyFiltersToTranscripts(state.transcriptsAll);

    resort();
    updatePositions();
    update();
  }

  function onPreferencesChanged() {
    const config = track.getPreferences();
    const filters = getFilterDict();
    const { isDark } = config;
    const theme = isDark ? 2 : 1;
    const scale = config[TRANSCRIPT_CL];

    const scaleTr = new ColorScale(scale, 8).setTheme(theme);
    const scaleExon = new ColorScale(scale, 8).setTheme(theme);
    const scaleCodon = new ColorScale(scale, 8).setTheme(theme).brighten(-0.5);

    const trHexes = scaleTr.hexes;
    const trVecs = scaleTr.vectors;
    const exonVecs = scaleExon.vectors;
    const codonVecs = scaleCodon.vectors;

    const hInd = sequenceRgbIndex[sequenceTypeIndex[HAPLO]];
    const tInd = sequenceRgbIndex[sequenceTypeIndex[TRIPLO]];
    const htInd = sequenceRgbIndex[sequenceTypeIndex[TRIPLO_HAPLO]];
    const nInd = sequenceRgbIndex[sequenceTypeIndex[NORMAL]];

    filters.haplo.setColor(trHexes[hInd]);
    filters.triplo.setColor(trHexes[tInd]);
    filters.haploTriplo.setColor(trHexes[htInd]);
    filters.normal.setColor(trHexes[nInd]);

    transcriptsRightMesh.uniforms.setColorMap(trVecs);
    transcriptsLeftMesh.uniforms.setColorMap(trVecs);

    sequencesRightMesh.uniforms.setColorMap(exonVecs);
    sequencesLeftMesh.uniforms.setColorMap(exonVecs);
    exonsRightMesh.uniforms.setColorMap(exonVecs);
    exonsLeftMesh.uniforms.setColorMap(exonVecs);

    codonsLeftMesh.uniforms.setColorMap(codonVecs);
    codonsRightMesh.uniforms.setColorMap(codonVecs);

    update();
  }

  function getGeneDict() {
    return state.geneDict;
  }

  function keepTranscriptForGene(transcript) {
    const { geneSymbol } = transcript || {};
    const geneTranscripts = state.geneDict.get(geneSymbol) || [];
    const others = geneTranscripts.filter((x) => x !== transcript);

    others.forEach((x) => x.unload());
    others.forEach((x) => state.transcriptDict.delete(x.id));
    others.forEach((x) => state.transcriptDict.delete(x.id));

    state.geneDict.set(geneSymbol, [transcript]);

    const allNew = state.transcriptsAll.filter((x) => !x.isExcluded);
    const filters = state.filters.map((x) => x.setTotalCount(0));

    calcFilterCounts(allNew);
    resort();
    updatePositions();
    update();
    track.setFilters([...filters]);
  }

  return {
    onInit,
    getGeneDict,
    onDataReceived,
    keepTranscriptForGene,
    onRangeChange,
    onFiltersChange,
    onReset,
    onSharedStateChange,
    onPreferencesChanged,
    onRangeStabilized: update,
    meshes: [
      codonsLeftMesh,
      codonsRightMesh,
      exonsLeftMesh,
      exonsRightMesh,
      sequencesRightMesh,
      sequencesLeftMesh,
      transcriptsRightMesh,
      transcriptsLeftMesh,
      codonTextsMesh,
    ],
  };
}

export default transcriptTrackController;
